import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext();

export const CURRENCIES = [
  { code: 'USD', symbol: '$',     name: 'US Dollar',           locale: 'en-US' },
  { code: 'EUR', symbol: '€',     name: 'Euro',                locale: 'de-DE' },
  { code: 'GBP', symbol: '£',     name: 'British Pound',       locale: 'en-GB' },
  { code: 'CAD', symbol: 'CA$',   name: 'Canadian Dollar',     locale: 'en-CA' },
  { code: 'AUD', symbol: 'A$',    name: 'Australian Dollar',   locale: 'en-AU' },
  { code: 'JPY', symbol: '¥',     name: 'Japanese Yen',        locale: 'ja-JP' },
  { code: 'CHF', symbol: 'CHF',   name: 'Swiss Franc',         locale: 'de-CH' },
  { code: 'CNY', symbol: '¥',     name: 'Chinese Yuan',        locale: 'zh-CN' },
  { code: 'INR', symbol: '₹',     name: 'Indian Rupee',        locale: 'en-IN' },
  { code: 'AED', symbol: 'AED',   name: 'UAE Dirham',          locale: 'ar-AE' },
  { code: 'SAR', symbol: 'SAR',   name: 'Saudi Riyal',         locale: 'ar-SA' },
  { code: 'KWD', symbol: 'KWD',   name: 'Kuwaiti Dinar',       locale: 'ar-KW' },
  { code: 'BHD', symbol: 'BHD',   name: 'Bahraini Dinar',      locale: 'ar-BH' },
  { code: 'QAR', symbol: 'QAR',   name: 'Qatari Riyal',        locale: 'ar-QA' },
  { code: 'OMR', symbol: 'OMR',   name: 'Omani Rial',          locale: 'ar-OM' },
  { code: 'JOD', symbol: 'JOD',   name: 'Jordanian Dinar',     locale: 'ar-JO' },
  { code: 'IQD', symbol: 'د.ع',  name: 'Iraqi Dinar',         locale: 'ar-IQ', rtl: true },
  { code: 'TRY', symbol: '₺',     name: 'Turkish Lira',        locale: 'tr-TR' },
  { code: 'MXN', symbol: 'MX$',   name: 'Mexican Peso',        locale: 'es-MX' },
  { code: 'ZAR', symbol: 'R',     name: 'South African Rand',  locale: 'en-ZA' },
];

export const STRIPE_UNSUPPORTED = new Set(['IQD']);

export const isStripeSupported = (currency) =>
  !STRIPE_UNSUPPORTED.has((currency || 'USD').toUpperCase());

export const CurrencyProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [orgCurrency, setOrgCurrency] = useState('USD');
  const [currencyLoading, setCurrencyLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setCurrencyLoading(false);
      return;
    }

    const isPortalUser = ['tenant', 'owner', 'vendor'].includes(user.role);

    if (isPortalUser) {
      // Portal users get currency injected at login via user.orgCurrency
      setOrgCurrency((user.orgCurrency || 'USD').toUpperCase());
      setCurrencyLoading(false);
    } else {
      api.get('/organization/settings')
        .then(res => {
          if (res.data?.success && res.data.organization?.currency) {
            setOrgCurrency(res.data.organization.currency.toUpperCase());
          }
        })
        .catch(() => {})
        .finally(() => setCurrencyLoading(false));
    }
  }, [isAuthenticated, user?.id, user?.orgCurrency]);

  const formatCurrency = (amount, overrideCurrency) => {
    const code = (overrideCurrency || orgCurrency || 'USD').toUpperCase();
    const meta = CURRENCIES.find(c => c.code === code);
    const locale = meta?.locale || 'en-US';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    }
  };

  const currencySymbol = CURRENCIES.find(c => c.code === orgCurrency)?.symbol || orgCurrency;

  return (
    <CurrencyContext.Provider value={{
      orgCurrency,
      setOrgCurrency,
      formatCurrency,
      currencySymbol,
      currencyLoading,
      CURRENCIES,
      isStripeSupported,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};

export default CurrencyProvider;
